import clsx from 'clsx';
import { Children, isValidElement, type ReactNode } from 'react';

export type AssistantBubbleProps = {
    readonly children: ReactNode;
    readonly className?: string;
    readonly timestamp?: number;
    readonly timeFormatter?: Intl.DateTimeFormat;
    readonly compact?: boolean;
    readonly streaming?: boolean;
};

export type AssistantBubbleTextProps = {
    readonly children: ReactNode;
    readonly compact?: boolean;
    readonly fullWidth?: boolean;
    readonly streaming?: boolean;
};

export type AssistantBubbleExtrasProps = {
    readonly children: ReactNode;
};

/** The blinking caret after streaming text. Markdown renders it inside its last block, so it shares the class. */
export const streamingCaretClassName =
    'ml-[1px] inline-block h-[1em] w-[2px] animate-pulse bg-text-primary align-[-2px]';

function AssistantBubbleText({ children, compact, fullWidth = false, streaming }: AssistantBubbleTextProps) {
    return (
        <div
            className={clsx(
                fullWidth ? 'max-w-full' : 'max-w-[92%]',
                'whitespace-pre-wrap text-text-primary [overflow-wrap:anywhere]',
                compact ? 'text-xs' : 'text-[13px]',
            )}
        >
            {children}
            {streaming && <span aria-hidden={true} className={streamingCaretClassName} />}
        </div>
    );
}

function AssistantBubbleExtras({ children }: AssistantBubbleExtrasProps) {
    return <div className="flex w-full max-w-[92%] flex-col gap-s">{children}</div>;
}

function hasSubcomponentChildren(children: ReactNode): boolean {
    return Children.toArray(children).some(
        (child) =>
            isValidElement(child) && (child.type === AssistantBubbleText || child.type === AssistantBubbleExtras),
    );
}

export function AssistantBubble({
    children,
    timestamp,
    timeFormatter,
    compact,
    streaming,
    className,
}: AssistantBubbleProps) {
    const composed = hasSubcomponentChildren(children);

    return (
        <div className={clsx('group flex w-full flex-col items-start gap-s', className)}>
            {composed ? (
                children
            ) : (
                <AssistantBubbleText compact={compact} streaming={streaming}>
                    {children}
                </AssistantBubbleText>
            )}
            {timestamp !== undefined && timeFormatter && (
                <p className="m-0 text-[11px] text-text-content-faint opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    {timeFormatter.format(new Date(timestamp))}
                </p>
            )}
        </div>
    );
}

AssistantBubble.Text = AssistantBubbleText;
AssistantBubble.Extras = AssistantBubbleExtras;
